import {
  Box,
  SpaceBetween,
  Header,
  Spinner,
  Alert,
  Grid,
  Badge,
} from "@cloudscape-design/components";
import { useParams, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useBreadcrumbs } from "../contexts/BreadcrumbContext";
import organizationService from "services/organizationService";
import {
  SSO_GENERIC_PROTOCOLS_LIST,
  SSO_PROVIDERS_LIST,
} from "configs/settings";
import AppConfig from "configs/AppConfig";

export default function SSOProviders() {
  const { setBreadcrumbs } = useBreadcrumbs();
  const { id } = useParams();
  const location = useLocation();
  const [organization, setOrganization] = useState(
    location.state?.organization || null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOrganizationData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const orgData = await organizationService.getOrganizationById(id);
      setOrganization(orgData);
      setLoading(false);
      setError(null);
    } catch (error) {
      console.error("Error fetching organization:", error);
      setError("Failed to load organization details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!organization && id) {
      fetchOrganizationData();
    } else if (organization) {
      setLoading(false);
    }
  }, [organization, id]);

  useEffect(() => {
    if (organization) {
      setBreadcrumbs([
        { text: AppConfig.APP_NAME, href: "/" },
        { text: "Organizations", href: "/organizations" },
        { text: organization.display_name, href: `/organizations/${id}` },
        { text: "Settings", href: `/organizations/${id}/settings` },
        { text: "SSO Providers", href: `/organizations/${id}/sso-providers` },
      ]);
    }

    // Cleanup when component unmounts
    return () => {
      setBreadcrumbs([]);
    };
  }, [organization, id, setBreadcrumbs]);


  return (
    <SpaceBetween size="l">
      {loading && (
        <Box padding={{ horizontal: "m", vertical: "l" }}>
          <Spinner size="large" />
          <Box color="text-status-info" fontSize="heading-s">
            Loading organization settings...
          </Box>
        </Box>
      )}

      {!loading && (error || !organization) && (
        <Box padding={{ horizontal: "m", vertical: "l" }}>
          <Alert type="error">{error || "Organization not found."}</Alert>
        </Box>
      )}

      {!loading && !error && organization && (
        <>
          <SpaceBetween size="xs" direction="horizontal" alignItems="center">
            <Header variant="h2">SSO Providers</Header>
            <Badge color="blue">coming soon</Badge>
          </SpaceBetween>

          <SpaceBetween size="s" alignItems="center">
            <Header variant="h1">Select your Identity Provider</Header>
            <SpaceBetween size="xs" alignItems="center">
              <p>
                This is how users will sign in to{" "}
                <span className="bold">{organization.display_name}</span>
              </p>
              <Grid
                gridDefinition={[
                  { colspan: { default: 12, xs: 6 }, offset: { xs: 3 } },
                ]}
              >
                <div>
                  <Grid
                    gridDefinition={SSO_PROVIDERS_LIST.map(() => ({
                      colspan: 4,
                    }))}
                  >
                    {SSO_PROVIDERS_LIST.map((logo, index) => (
                      <div key={index} className="logo-container">
                        <img
                          src={logo.url}
                          alt={logo.name}
                          className="logo-image"
                        />
                      </div>
                    ))}
                  </Grid>
                </div>
              </Grid>
            </SpaceBetween>
          </SpaceBetween>
          <SpaceBetween size="xs">
            <p className="text-center">
              If you don't see your identity provider, select one of the generic
              <br />
              protocols below to connect with your provider
            </p>
            <Grid
              gridDefinition={[
                {
                  colspan: { default: 8, xs: 4 },
                  offset: { default: 2, xs: 4 },
                },
              ]}
            >
              <div>
                <Grid
                  gridDefinition={SSO_GENERIC_PROTOCOLS_LIST.map(() => ({
                    colspan: 6,
                  }))}
                >
                  {SSO_GENERIC_PROTOCOLS_LIST.map((logo, index) => (
                    <div key={index} className="logo-container">
                      <img
                        src={logo.url}
                        alt={logo.name}
                        className="logo-image"
                      />
                    </div>
                  ))}
                </Grid>
              </div>
            </Grid>
          </SpaceBetween>
        </>
      )}
    </SpaceBetween>
  );
}
